import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sendMarketingMessageStream, resetSession, submitEarlyAccess, fetchSuggestedQuestions, type EarlyAccessPayload } from '../../services/marketingChatService';
import logo from '../../assets/images/M_Multicolor.svg';

const EARLY_ACCESS_TOKEN = '$$SHOW_EARLY_ACCESS_FORM$$';

interface Message {
  id: string | number;
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
}

const SUGGESTION_CARDS = [
  { id: 'what-is', text: 'Tell me what InsuranceAgency.AI is' },
  { id: 'tasks', text: 'Show me what tasks this handles day to day' },
  { id: 'systems', text: 'Show me how this works with my existing systems' },
  { id: 'different', text: 'Tell me why this is different from ChatGPT' },
  { id: 'producers', text: 'Tell me how this affects my producers' },
  { id: 'early-access', text: 'Help me request early access' },
];

function formatTime(): string {
  const now = new Date();
  let h = now.getHours();
  const m = now.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

function getFriendlyError(err: Error): string {
  const msg = err?.message || '';
  if (msg.includes('429')) return 'Rate limit reached. Please wait a few seconds and try again.';
  if (msg.includes('500')) return 'The server encountered an issue. Please try again in a few seconds.';
  if (msg.includes('503')) return 'The service is temporarily unavailable. Please try again shortly.';
  if (msg.includes('401') || msg.includes('403')) return 'Access denied. Please contact your administrator.';
  if (msg.includes('404')) return 'The chat service could not be reached. Please check your connection.';
  if (msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network')) return 'Network error. Please check your internet connection and try again.';
  return 'Something went wrong. Please try again.';
}

/* ── Inline Early Access Form (rendered inside chat) ── */
const InlinEarlyAccessForm: React.FC = () => {
  const [form, setForm] = useState({ fullName: '', email: '', agencyName: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const payload: EarlyAccessPayload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        agencyName: form.agencyName.trim(),
        isCurrentCustomer: false,
      };
      const result = await submitEarlyAccess(payload);
      if (result.duplicate) {
        setDuplicate(true);
      }
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mkt-ea-inline-success">
        <div className="mkt-ea-inline-success__icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <div className="mkt-ea-inline-success__text">
          <strong className="mkt-ea-inline-success__title">
            {duplicate ? "You're already on the list." : "You're on the waitlist!"}
          </strong>
          <p className="mkt-ea-inline-success__sub">
            We'll be in touch with details about beta access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mkt-ea-card mkt-ea-card--inline">
      <form onSubmit={handleSubmit}>
        <div className="mkt-ea-card__group">
          <input type="text" name="fullName" placeholder="Full Name" value={form.fullName} onChange={handleChange} required autoComplete="name" />
        </div>
        <div className="mkt-ea-card__group">
          <input type="email" name="email" placeholder="Email Address" value={form.email} onChange={handleChange} required autoComplete="email" />
        </div>
        <div className="mkt-ea-card__group">
          <input type="text" name="agencyName" placeholder="Agency Name (optional)" value={form.agencyName} onChange={handleChange} autoComplete="organization" />
        </div>
        {error && <div className="mkt-ea-card__error">{error}</div>}
        <button className="mkt-ea-card__cta" type="submit" disabled={submitting || !form.fullName.trim() || !form.email.trim()}>
          {submitting ? 'Submitting...' : 'Join the Waitlist'}
        </button>
      </form>
    </div>
  );
};

let msgCounter = 0;

interface MarketingChatAreaProps {
  collapsed: boolean;
  rightPanelOpen?: boolean;
  onMenuClick: () => void;
  onToggleRightPanel: () => void;
  onCollapseChat: () => void;
  onExpandChat: () => void;
}

const MarketingChatArea: React.FC<MarketingChatAreaProps> = ({
  collapsed,
  rightPanelOpen,
  onMenuClick,
  onToggleRightPanel,
  onCollapseChat,
  onExpandChat,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userScrolledUp = useRef(false);

  const handleScroll = useCallback(() => {
    const el = windowRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUp.current = distFromBottom > 100;
  }, []);

  useEffect(() => {
    if (!userScrolledUp.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  // Suggested questions — debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (inputValue.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const results = await fetchSuggestedQuestions(inputValue.trim());
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setActiveIndex(-1);
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputValue]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showWelcome = messages.length === 0 && !loading;

  const handleSend = useCallback(async (question: string) => {
    if (!question.trim() || loading) return;

    setSuggestions([]);
    setShowSuggestions(false);

    const userId = `user-${++msgCounter}`;
    setMessages((prev) => [...prev, { id: userId, role: 'user', content: question.trim() }]);
    setInputValue('');
    setLoading(true);

    const msgId = Date.now();
    setMessages((prev) => [
      ...prev,
      { id: msgId, role: 'assistant', content: '', streaming: true },
    ]);

    await sendMarketingMessageStream(
      question,
      (chunk) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === msgId ? { ...msg, content: msg.content + chunk } : msg
          )
        );
      },
      () => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === msgId ? { ...msg, streaming: false } : msg
          )
        );
        setLoading(false);
      },
      (err) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === msgId
              ? { ...msg, content: getFriendlyError(err), streaming: false }
              : msg
          )
        );
        setLoading(false);
      }
    );
  }, [loading]);

  const handleCardClick = useCallback((text: string) => {
    handleSend(text);
  }, [handleSend]);

  const handleInputSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    handleSend(inputValue);
  }, [handleSend, inputValue]);

  const handleSelectSuggestion = useCallback((question: string) => {
    handleSend(question);
  }, [handleSend]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        handleSelectSuggestion(suggestions[activeIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(inputValue);
    }
  }, [handleSend, handleSelectSuggestion, inputValue, showSuggestions, suggestions, activeIndex]);

  const handleNewChat = useCallback(() => {
    resetSession();
    setMessages([]);
    setLoading(false);
  }, []);

  /* ── Collapsed state ── */
  if (collapsed) {
    return (
      <div className="mkt-center mkt-center--collapsed">
        <button className="mkt-chat-expand-btn" onClick={onExpandChat} aria-label="Expand chat">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className={`mkt-center${rightPanelOpen ? ' mkt-center--blurred' : ''}`}>
      {/* ── Header ── */}
      <header className="mkt-topbar">
        <div className="mkt-topbar__left">
          <button className="mkt-topbar__menu" onClick={onMenuClick} aria-label="Toggle menu">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>
          <div className="mkt-topbar__brand">
            <img src={logo} alt="InsuranceAgency.AI" width="28" height="28" className="mkt-topbar__logo" />
            <div className="mkt-topbar__text">
              <div className="mkt-topbar__title">InsuranceAgency.AI</div>
              <div className="mkt-topbar__subtitle">Your agency's AI operating system</div>
            </div>
          </div>
        </div>
        <div className="mkt-topbar__right">
          <button className="mkt-topbar__icon-btn mkt-topbar__panel-btn" onClick={onToggleRightPanel} aria-label="Toggle early access panel">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="15" y1="3" x2="15" y2="21" />
            </svg>
          </button>
          <a className="mkt-topbar__signin" href="/login">
            Sign In
          </a>
          <button className="mkt-topbar__icon-btn mkt-topbar__collapse-btn" onClick={onCollapseChat} aria-label="Collapse chat">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </header>

      {/* ── Content Area ── */}
      <div className="chat-window" ref={windowRef} onScroll={handleScroll}>
        {showWelcome ? (
          <div className="chat-empty">
            <div className="chat-empty-text">
              <img src={logo} alt="InsuranceAgency.AI" width="56" height="56" className="chat-empty-icon" />
              <p className="chat-empty-heading">
                The Operating System Independent<br />
                Agents Have Been Waiting For
              </p>
              <p className="chat-empty-sub">
                The future of your insurance agency starts here. Ask InsuranceAgency.AI<br />
                anything about the platform and see what's possible.
              </p>
            </div>

            <div className="suggested-prompts">
              {SUGGESTION_CARDS.map((card, i) => (
                <motion.button
                  key={card.id}
                  className="suggested-prompt-btn"
                  onClick={() => handleCardClick(card.text)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.35 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {card.text}
                </motion.button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => {
              const isUser = msg.role === 'user';
              const showThinking = !isUser && msg.streaming && !msg.content;
              const containsToken = !isUser && msg.content.includes(EARLY_ACCESS_TOKEN);
              const hasForm = containsToken && !msg.streaming;
              const displayContent = containsToken
                ? msg.content.replace(EARLY_ACCESS_TOKEN, '').trimEnd()
                : msg.content.trimEnd();

              return (
                <div key={msg.id} className={`mkt-msg ${isUser ? 'mkt-msg--user' : 'mkt-msg--assistant'}`}>
                  <div className="mkt-msg__card">
                    <div className="mkt-msg__header">
                      <div className={`mkt-msg__avatar ${isUser ? 'mkt-msg__avatar--user' : 'mkt-msg__avatar--ai'}`}>
                        {isUser ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        ) : (
                          <img src={logo} alt="InsuranceAgency.AI" width="32" height="32" style={{ display: 'block' }} />
                        )}
                      </div>
                      <span className="mkt-msg__name">{isUser ? 'You' : 'InsuranceAgency.AI'}</span>
                      <span className="mkt-msg__time">{formatTime()}</span>
                    </div>
                    <div className={`mkt-msg__body${msg.streaming ? ' mkt-msg__body--streaming' : ''}`}>
                      {showThinking ? (
                        <div className="mkt-msg__thinking">
                          <img src={logo} alt="" width="16" height="16" style={{ borderRadius: '3px' }} className="mkt-msg__thinking-icon" />
                          <span>Thinking...</span>
                        </div>
                      ) : isUser ? (
                        <p>{msg.content}</p>
                      ) : (
                        <>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
                          {msg.streaming && <span className="mkt-msg__cursor" />}
                        </>
                      )}
                    </div>
                    {hasForm && <InlinEarlyAccessForm />}
                  </div>
                </div>
              );
            })}

            {/* Loading dots when waiting for first chunk */}
            {loading && !messages.some((m) => m.streaming) && (
              <div className="chat-loading">
                <div className="chat-loading-inner">
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* ── Chat Input (matches reference ChatInput) ── */}
      <div className="mkt-input-area" ref={wrapperRef}>
        {showSuggestions && (
          <div className="mkt-suggestions">
            {suggestions.map((q, i) => (
              <button
                key={i}
                className={`mkt-suggestions__item${i === activeIndex ? ' mkt-suggestions__item--active' : ''}`}
                onClick={() => handleSelectSuggestion(q)}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <span>{q}</span>
              </button>
            ))}
          </div>
        )}
        <form className="mkt-input" onSubmit={handleInputSubmit}>
          <div className="mkt-input__container">
            <span className="mkt-input__slash">/</span>
            <textarea
              ref={inputRef}
              className="mkt-input__field"
              rows={1}
              placeholder="Try asking a question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
          </div>
          <button
            className="mkt-input__send"
            type="submit"
            disabled={loading || !inputValue.trim()}
            aria-label="Send message"
          >
            {loading ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.4">
                  <animate attributeName="opacity" values="0.4;1;0.4" dur="1s" repeatCount="indefinite" begin="0s"/>
                </circle>
                <circle cx="6" cy="12" r="3" fill="currentColor" opacity="0.4">
                  <animate attributeName="opacity" values="0.4;1;0.4" dur="1s" repeatCount="indefinite" begin="0.2s"/>
                </circle>
                <circle cx="18" cy="12" r="3" fill="currentColor" opacity="0.4">
                  <animate attributeName="opacity" values="0.4;1;0.4" dur="1s" repeatCount="indefinite" begin="0.4s"/>
                </circle>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MarketingChatArea;
